import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Card, Button, Table, message, Space, Typography, Divider, Popconfirm, Spin, Alert, 
  Checkbox, Row, Col, Input
} from 'antd';
import { ArrowLeftOutlined, PlusOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import { teacherAPI, classAPI } from '../../services/api';
import api from '../../services/api';

const { Title, Text } = Typography;
const { Search } = Input;

const TeacherClassBinding = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const targetSchoolId = searchParams.get('schoolId');
  const [teacher, setTeacher] = useState(null);
  const [classes, setClasses] = useState([]);
  const [availableClasses, setAvailableClasses] = useState([]);
  const [selectedClassIds, setSelectedClassIds] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [loading, setLoading] = useState(true);
  const [btnLoading, setBtnLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchTeacherInfo = async () => {
    try {
      const response = await teacherAPI.getById(id);
      if (response.data.success) {
        setTeacher(response.data.data);
      } else {
        setError(response.data.message || '获取教师信息失败');
      }
    } catch (error) {
      setError(error.response?.data?.message || '获取教师信息失败');
      console.error('获取教师信息失败:', error);
    }
  };

  const fetchTeacherClasses = async () => {
    try {
      const response = await api.get(`/teacher-classes/teacher/${id}`, {
        params: targetSchoolId ? { schoolId: targetSchoolId } : undefined
      });
      if (response.data.success) {
        setClasses(response.data.data);
      } else {
        setError(response.data.message || '获取教师班级失败');
      }
    } catch (error) {
      setError(error.response?.data?.message || '获取教师班级失败');
      console.error('获取教师班级失败:', error);
    }
  };

  const fetchAvailableClasses = async () => {
    try {
      // 获取当前教师所在学校的所有班级
      const schoolId = targetSchoolId || teacher.school_id;
      const response = await classAPI.getAll({ schoolId, pageSize: 200 });
      
      if (response.data.success) {
        // 过滤出当前教师学校的班级，并排除已绑定的班级
        const allClasses = response.data.data.filter(c => String(c.school_id) === String(schoolId));
        const boundClassIds = classes.map(c => c.id);
        const available = allClasses.filter(c => !boundClassIds.includes(c.id));
        setAvailableClasses(available);
      } else {
        message.error(response.data.message || '获取可用班级失败');
      }
    } catch (error) {
      message.error(error.response?.data?.message || '获取可用班级失败');
      console.error('获取可用班级失败:', error);
    }
  };

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      await fetchTeacherInfo();
      await fetchTeacherClasses();
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setError('加载数据失败，请重试');
      console.error('加载数据失败:', error);
    }
  };

  useEffect(() => {
    loadData();
  }, [id]);

  useEffect(() => {
    if (teacher && (targetSchoolId || teacher.school_id)) {
      fetchAvailableClasses();
    }
  }, [teacher, classes, targetSchoolId]);

  const handleBindClass = async () => {
    if (selectedClassIds.length === 0) {
      message.warning('请选择要绑定的班级');
      return;
    }

    setBtnLoading(true);
    try {
      // 批量绑定班级
      const promises = selectedClassIds.map(classId => 
        api.post('/teacher-classes', {
          teacherId: id,
          classId: classId
        })
      );

      await Promise.all(promises);
      message.success(`成功绑定${selectedClassIds.length}个班级`);
      
      // 刷新班级列表
      fetchTeacherClasses();
      // 重置选择
      setSelectedClassIds([]);
    } catch (error) {
      message.error(error.response?.data?.message || '班级绑定失败');
      console.error('班级绑定失败:', error);
    } finally {
      setBtnLoading(false);
    }
  };

  const handleUnbindClass = async (bindingId) => {
    try {
      const response = await api.delete(`/teacher-classes/${bindingId}`);
      
      if (response.data.success) {
        message.success('班级解绑成功');
        // 刷新班级列表
        fetchTeacherClasses();
      } else {
        message.error(response.data.message || '班级解绑失败');
      }
    } catch (error) {
      message.error(error.response?.data?.message || '班级解绑失败');
      console.error('班级解绑失败:', error);
    }
  };

  // 处理班级选择
  const handleClassSelection = (classId, checked) => {
    if (checked) {
      setSelectedClassIds([...selectedClassIds, classId]);
    } else {
      setSelectedClassIds(selectedClassIds.filter(id => id !== classId));
    }
  };

  // 处理全选/取消全选
  const handleSelectAll = (checked) => {
    if (checked) {
      const filteredClasses = getFilteredClasses();
      setSelectedClassIds(filteredClasses.map(c => c.id));
    } else {
      setSelectedClassIds([]);
    }
  };

  // 获取过滤后的班级列表
  const getFilteredClasses = () => {
    if (!searchKeyword) return availableClasses;
    return availableClasses.filter(c => 
      c.name.toLowerCase().includes(searchKeyword.toLowerCase()) ||
      (c.grade && c.grade.toLowerCase().includes(searchKeyword.toLowerCase()))
    );
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: '班级名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '年级',
      dataIndex: 'grade',
      key: 'grade',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <span style={{ color: status === 'active' ? 'green' : 'red' }}>
          {status === 'active' ? '激活' : '未激活'}
        </span>
      ),
    },
    {
      title: '班级角色',
      dataIndex: 'teacher_role',
      key: 'teacher_role',
      render: (role) => ({
        owner: '班级所有者',
        creator: '班级创建者',
        head_teacher: '班主任',
        teacher: '任课教师'
      }[role] || role || '任课教师'),
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => {
        const protectedOwner = ['owner', 'creator'].includes(record.teacher_role);
        if (protectedOwner) {
          return <Button disabled size="small" title="请先转移班级所有权">需先转移所有权</Button>;
        }
        return (
          <Popconfirm
            title="确定要解除与该班级的绑定吗?"
            onConfirm={() => handleUnbindClass(record.binding_id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="primary" danger icon={<DeleteOutlined />} size="small">
              解绑
            </Button>
          </Popconfirm>
        );
      },
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px' }}>
        <Alert type="error" message={error} banner />
        <div style={{ marginTop: '20px', textAlign: 'center' }}>
          <Button onClick={() => navigate('/teachers')} icon={<ArrowLeftOutlined />}>
            返回教师列表
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Card
        title={
          <div>
            <Button 
              type="link" 
              icon={<ArrowLeftOutlined />} 
              style={{ marginRight: 10, padding: 0 }}
              onClick={() => navigate('/teachers')}
            />
            教师班级绑定管理
          </div>
        }
      >
        {teacher && (
          <div>
            <div style={{ marginBottom: 20 }}>
              <Title level={4}>教师信息</Title>
              <p><Text strong>姓名：</Text> {teacher.name}</p>
              <p><Text strong>学科：</Text> {teacher.subject}</p>
              <p><Text strong>学校：</Text> {teacher.school_name}</p>
            </div>
            
            <Divider />
            
            <div style={{ marginBottom: 20 }}>
              <Title level={4}>绑定新班级</Title>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Row justify="space-between" align="middle">
                  <Col>
                    <Search
                      placeholder="搜索班级"
                      onSearch={setSearchKeyword}
                      onChange={(e) => setSearchKeyword(e.target.value)}
                      style={{ width: 200 }}
                    />
                  </Col>
                  <Col>
                    <Space>
                      <Checkbox
                        indeterminate={selectedClassIds.length > 0 && selectedClassIds.length < getFilteredClasses().length}
                        checked={getFilteredClasses().length > 0 && selectedClassIds.length === getFilteredClasses().length}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                      >
                        全选
                      </Checkbox>
                      <Button 
                        type="primary" 
                        icon={<PlusOutlined />}
                        loading={btnLoading}
                        onClick={handleBindClass}
                        disabled={selectedClassIds.length === 0}
                      >
                        绑定选中班级 ({selectedClassIds.length})
                      </Button>
                    </Space>
                  </Col>
                </Row>
                
                <Card 
                  title={`可绑定班级 (${getFilteredClasses().length})`}
                  size="small"
                  style={{ maxHeight: 400, overflow: 'auto' }}
                >
                  {getFilteredClasses().length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                      {searchKeyword ? '没有找到匹配的班级' : '暂无可绑定的班级'}
                    </div>
                  ) : (
                    <Row gutter={[16, 8]}>
                      {getFilteredClasses().map(classItem => (
                        <Col span={8} key={classItem.id}>
                          <Card 
                            size="small" 
                            hoverable
                            style={{ 
                              cursor: 'pointer',
                              border: selectedClassIds.includes(classItem.id) ? '2px solid #1890ff' : '1px solid #d9d9d9'
                            }}
                            onClick={() => handleClassSelection(classItem.id, !selectedClassIds.includes(classItem.id))}
                          >
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              <Checkbox
                                checked={selectedClassIds.includes(classItem.id)}
                                onChange={(e) => handleClassSelection(classItem.id, e.target.checked)}
                                style={{ marginRight: 8 }}
                              />
                              <div>
                                <div style={{ fontWeight: 'bold' }}>{classItem.name}</div>
                                {classItem.grade && (
                                  <div style={{ fontSize: '12px', color: '#666' }}>
                                    {classItem.grade}
                                  </div>
                                )}
                              </div>
                            </div>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  )}
                </Card>
              </Space>
            </div>
            
            <Divider />

            <div>
              <Title level={4}>已绑定班级</Title>
              <Table 
                columns={columns} 
                dataSource={classes} 
                rowKey="id"
                pagination={{ pageSize: 10 }}
              />
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default TeacherClassBinding; 
